import type { Updater } from '@tanstack/vue-table'
import type { Ref } from 'vue'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
  ref.value
    = typeof updaterOrValue === 'function'
      ? updaterOrValue(ref.value)
      : updaterOrValue
}

import { CalendarDate } from '@internationalized/date';
import { jwtDecode } from "jwt-decode";

// Digits only, max 10; drops a leading country code 1 from pasted 11-digit numbers
export const normalizePhone = (value: any) => {
  let digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.slice(0, 10);
};

// Progressive display format: (5, (555) 5, (555) 555-5555
export const formatPhoneInput = (value: any) => {
  const digits = normalizePhone(value);
  if (digits.length < 4) return digits ? `(${digits}` : '';
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// disalbe all ts errors for this file
export const formatPhoneNumber = (phoneNumber: any) => {
  const cleaned = phoneNumber?.replace(/\D/g, '');

  if (!cleaned || cleaned.length !== 10) {
    return phoneNumber; // Return original if invalid
  }

  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

export const isDateInPast = (dateString: any) => {
  const inputDate = new Date(dateString);
  const currentDate = new Date();
  return inputDate < currentDate;
};

export const formatCheckinCheckout = (date: any) => {
  const d = new Date(date + 'T00:00:00')

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
};

export const formatDateTime = (dateString: any) => {
  if (!dateString) return '';
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    hour12: true,
  }).format(date)
};

export const monthDayYear = (dateString: any) => {
  if (!dateString) return '';
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
};

export const hoursMinutes = (dateString: any) => {
  if (!dateString) return '';
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(date)
};

export const formatToMMDDYYYY = (date: any) => {
  if (!date) return '';
  const d = new Date(date);
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatTime = (time24: any) => {
  if (!time24) return '';

  const [hours24, minutes] = time24.split(':');
  let hours12 = parseInt(hours24, 10) % 12;

  hours12 = hours12 === 0 ? 12 : hours12;

  const period = parseInt(hours24, 10) >= 12 ? 'PM' : 'AM';

  return `${hours12}:${minutes} ${period}`;
}

export const exportTableToCSV = (filename: any, headers: any) => {
  const csv = [];

  // Add headers from the ref
  csv.push(headers.join(','));

  // Process data rows
  const rows = document.querySelectorAll("table tbody tr"); // Skip header row

  for (const row of rows) {
      const cols = row.querySelectorAll("td");
      const rowData = [];

      for (const col of cols) {
          const value = col.textContent.trim();
          // Escape quotes and wrap in quotes if contains comma or newline
          rowData.push(value.includes(',') || value.includes('\n') || value.includes('"')
              ? `"${value.replace(/"/g, '""')}"`
              : value
          );
      }

      csv.push(rowData.join(","));
  }

  const csvString = csv.join("\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename || "export.csv";
  link.click();
};

export const address = (address: any) => {
  if (!address) return '';
  const { address1, address2, city, state, zipCode } = address;
  const { address_1, address_2, zip_code } = address;

  return `
    ${address1 || address_1},
    ${(address2 || address_2) ? (address2 || address_2)+ ',' : ''}
    ${city},
    ${state}
    ${zipCode || zip_code}`
};

const formatStringForMap = (str: any) => str.split(" ").join("+");
const googleMapsBaseUrl = "https://www.google.com/maps/place/";

export const addressToMapLink = (address: any) => {
  if (!address) return '#';
  const { address1, address_1, address2, address_2, city, state, zipCode, zip_code } = address;
  const addressIfExists = () => (address2 || address_2) ? `${address2 || address_2}+` : ''
  const cityStateZip = `${city}+${state}+${zipCode || zip_code}`
  return `${googleMapsBaseUrl}${formatStringForMap((address1 || address_1))}+${addressIfExists()}${cityStateZip}`;
}

export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536
};

export const createDateObject = (date: any) => {
  const [month, day, year] = date.split("/").map(Number);

  const calendarDate = new CalendarDate(year, month, day);
  return calendarDate;
}

export const checkBreakpoint = (innerWidth: any) => innerWidth < breakpoints.md;

// @ts-ignore
export const isValidToken = (token: any) => {
  try {
    const decoded = jwtDecode(token);
    return decoded && decoded.exp && decoded.exp > Date.now() / 1000;
  } catch (e) {
    return false;
  }
}

export const devSignupForm = {
  first_name: 'CHANGE ME',
  last_name: 'CHANGE ME',
  email: 'change@me.com',
  phone: '3035136379',
  password: 'what a password 1!!@',
  signup_code: 'NEW20',
  marketing_opt_in: false,
  role: 'PARKER'
};

export const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const weekdays = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

export const ownerDashboardRoles = ['CPO', 'PPO', 'TEAM_MANAGER', 'TEAM_MEMBER'];
export const parkerOrAnonymousRoles = ['ANONYMOUS', 'PARKER'];
export const ownerRoles = ['CPO', 'PPO'];
export const teamRoles = ['TEAM_MANAGER', 'TEAM_MEMBER'];
export const isParkerOrAnonymous = (role: any) => parkerOrAnonymousRoles?.includes(role);
export const isTeamUser = (role: any) => teamRoles?.includes(role);
export const accessOwnerDashboard = (role: any) => [...ownerRoles, ...teamRoles].includes(role);
export const accessOwnerPlusManager = (role: any) => [...ownerRoles, 'TEAM_MANAGER'].includes(role);
// Drivers booked on site with only a phone number get a stand-in address that never receives mail
export const isOnsiteEmail = (email: any) => typeof email === 'string' && email.toLowerCase().endsWith('@onsite.spotsurfer.com');
export const canSeeAnyDashboard = (role: any) => {
  const roles = ['PARKER', ...ownerRoles, ...teamRoles];

  if (role === 'PARKER') return true;
  if (!roles.includes(role)) return false;
  // @ts-ignore
  if (!usePlan().isPlatinum && !window?.__ss_config?.isMain) return false;

  return true;
}

// Spots are in Colorado, so "today" and clock times follow Denver time wherever the visitor is
export const LOT_TIME_ZONE = 'America/Denver';
export const lotToday = () => new Date().toLocaleDateString('en-US', { timeZone: LOT_TIME_ZONE, month: '2-digit', day: '2-digit', year: 'numeric' });
export const lotClock = (date) => date.toLocaleTimeString('en-US', { timeZone: LOT_TIME_ZONE, hour: 'numeric', minute: '2-digit' });
